import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import OperateurDemandeDetail from "./OperateurDemandeDetail";

const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

function labelFromItemSlug(item) {
  switch (item) {
    case "demandes-transmises":
      return "Demandes transmises";
    case "sans-affectation":
      return "Sans Affectation";
    case "eaci":
      return "EACI";
    case "demandes-orientees":
      return "Demandes orientées";
    case "demandes-annulees":
      return "Demandes annulées";
    case "demandes-transferees":
      return "Demandes transférées";
    case "liste-attente":
      return "Liste d’attente";
    case "flechage":
      return "Fléchage";
    case "retour-decision-cds":
      return "Retour décision CDS";
    default:
      return item;
  }
}

function statutFromItemSlug(item) {
  switch (item) {
    case "sans-affectation":
      return "Sans Affectation";
    case "eaci":
      return "EACI";
    case "demandes-orientees":
      return "Demandes orientées";
    case "demandes-annulees":
      return "Demandes annulées";
    case "demandes-transferees":
      return "Demandes transférées";
    case "liste-attente":
      return "Liste d'attente";
    case "flechage":
      return "Fléchage";
    case "retour-decision-cds":
      return "Retour décision CDS";
    default:
      return "";
  }
}

function norm(s) {
  return (s ?? "")
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function pillClassFromStatut(statut) {
  const s = norm(statut);
  if (!s) return "pill pill--muted";
  if (s === norm("Sans Affectation")) return "pill pill--muted";
  if (s === norm("EACI")) return "pill pill--eaci";
  if (s === norm("Fléchage") || s === norm("Flechage")) return "pill pill--flechage";
  if (s === norm("Retour décision CDS")) return "pill pill--retour";
  if (s === norm("Demandes orientées")) return "pill pill--success";
  if (s === norm("Liste d'attente")) return "pill pill--retour";
  if (s === norm("Demandes annulées")) return "pill pill--danger";
  if (s === norm("Demandes transférées") || s === norm("Demandes transferees"))
    return "pill pill--muted";
  return "pill";
}

// couleur opérateur (comme page d'accueil / retour EACI)
function operateurColor(op) {
  const o = (op || "").toString().trim().toUpperCase();
  if (o === "AMEL") return "red";
  if (o === "LAMIA") return "#A020F0";
  if (o === "CLAUDIA") return "blue";
  if (o === "SOLENNE") return "orange";
  if (o === "MORGAN") return "green";
  if (o === "ELISSA") return "pink";
  return "";
}

function safeTime(raw) {
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

export default function OperateurItemPage({ viewerName }) {
  const navigate = useNavigate();
  const params = useParams();

  const name = decodeURIComponent(params.name || "");
  const item = params.item || "";
  const demandeId = params.demandeId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [demandes, setDemandes] = useState([]);
  const [demandeDetail, setDemandeDetail] = useState(null);

  const [searchText, setSearchText] = useState("");

  const title = useMemo(() => labelFromItemSlug(item), [item]);

  const refreshList = async () => {
    setError("");
    setLoading(true);

    try {
      const statut = statutFromItemSlug(item);

      const qs = new URLSearchParams();
      qs.set("operateur", name);

      if (item !== "demandes-transmises" && statut) {
        qs.set("statut_demande", statut);
      }

      const res = await fetch(`${API}/api/demandes?${qs.toString()}`);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Erreur chargement");
      }

      const data = await res.json();
      const arr = Array.isArray(data) ? data : [];

      // Fléchage d'ELISSA
      if (item === "flechage" && norm(name) === norm("ELISSA")) {
        const list = arr
          .filter(
            (d) =>
              norm(d.flechage) === norm("ELISSA") &&
              norm(d.statut_demande) === norm("Fléchage")
          )
          // ✅ plus ancien en haut, plus récent en bas
          .sort((a, b) => {
            const ta =
              safeTime(a?.flechage_at) ||
              safeTime(a?.date_premiere_transmission) ||
              safeTime(a?.createdAt);
            const tb =
              safeTime(b?.flechage_at) ||
              safeTime(b?.date_premiere_transmission) ||
              safeTime(b?.createdAt);
            return ta - tb;
          });

        setDemandes(list);
        return;
      }

      const onlyThisOperateur = arr.filter((d) => norm(d.operateur) === norm(name));

      // Demandes transmises = sans statut + non fléchées
      if (item === "demandes-transmises") {
        const transmises = onlyThisOperateur.filter((d) => {
          const st = norm(d.statut_demande);
          const isTransmise = st === "" || st === norm("Demandes transmises");
          return isTransmise && norm(d.flechage) === "";
        });
        setDemandes(transmises);
        return;
      }

      const finalList =
        item !== "demandes-transmises" && statut
          ? onlyThisOperateur.filter((d) => norm(d.statut_demande) === norm(statut))
          : onlyThisOperateur;

      setDemandes(finalList);
    } catch (e) {
      console.error(e);
      setError("Impossible de charger les demandes.");
      setDemandes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDetail = async (id) => {
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/demandes/${id}`);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Erreur chargement détail");
      }
      const data = await res.json();
      setDemandeDetail(data);
    } catch (e) {
      console.error(e);
      setError("Impossible de charger la demande.");
      setDemandeDetail(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (demandeId) {
      fetchDetail(demandeId);
      return;
    }

    setDemandeDetail(null);
    refreshList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, item, demandeId]);

  // ✅ Filtre + tri : prioritaire en haut (tri stable)
  const demandesAffichees = useMemo(() => {
    const q = norm(searchText);

    const base = !q
      ? demandes
      : demandes.filter((d) => {
          const nom = norm(d.nom_naissance);
          const prenom = norm(d.prenom);
          const numero = norm(d.numero_demande);
          return nom.includes(q) || prenom.includes(q) || numero.includes(q);
        });

    return base
      .map((d, idx) => ({ d, idx }))
      .sort((a, b) => {
        const pa = a.d?.prioritaire ? 1 : 0;
        const pb = b.d?.prioritaire ? 1 : 0;
        if (pb !== pa) return pb - pa; // ✅ prioritaire d'abord
        return a.idx - b.idx; // stable
      })
      .map((x) => x.d);
  }, [demandes, searchText]);

  if (demandeId) {
    return (
      <div className="content">
        <button
          onClick={() => navigate(`/operateur/${encodeURIComponent(name)}/${item}`)}
          style={{
            marginBottom: 12,
            padding: "10px 16px",
            borderRadius: 8,
            border: "1px solid #ddd",
            cursor: "pointer",
          }}
        >
          ← Retour
        </button>

        {loading ? <p>Chargement...</p> : null}
        {error ? <p style={{ color: "red" }}>{error}</p> : null}

        {!loading && !error ? (
          <OperateurDemandeDetail
            demande={demandeDetail}
            onBack={() => navigate(-1)}
            viewerName={viewerName}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="content">
      <div className="page-header">
        <div>
          <button
            onClick={() => navigate(`/operateur/${encodeURIComponent(name)}`)}
            style={{
              marginBottom: 12,
              padding: "10px 16px",
              borderRadius: 8,
              border: "1px solid #ddd",
              cursor: "pointer",
            }}
          >
            ← Retour
          </button>

          <h2 style={{ marginTop: 0 }}>
            {name} — {title}
          </h2>
          <div className="page-subtitle">Recherche + accès rapide aux dossiers</div>
        </div>
      </div>

      <input
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        placeholder="Rechercher (nom, prénom, numéro)…"
        style={{
          width: "100%",
          maxWidth: 520,
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid #ddd",
          margin: "10px 0 14px",
        }}
      />

      {loading ? <p>Chargement...</p> : null}
      {error ? <p style={{ color: "red" }}>{error}</p> : null}

      {!loading && !error && demandesAffichees.length === 0 ? <p>Aucune demande.</p> : null}

      {!loading && !error && demandesAffichees.length > 0 ? (
        <div style={{ display: "grid", gap: 12 }}>
          {demandesAffichees.map((d) => {
            const statut = d.statut_demande || "";
            const nomPrenom = `${d.nom_naissance || ""} ${d.prenom || ""}`.trim();

            return (
              <div
                key={d._id}
                className="card card--pad"
                onClick={() =>
                  navigate(`/operateur/${encodeURIComponent(name)}/${item}/${d._id}`)
                }
                style={{
                  cursor: "pointer",
                  transition: "transform .08s ease, box-shadow .18s ease",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                  }}
                >
                  <div style={{ fontWeight: 950, fontSize: 16, letterSpacing: "-0.2px" }}>
                    {nomPrenom || "—"}
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {/* ✅ alerte : point d’exclamation */}
                    {d?.alerte ? (
                      <span
                        title="Alerte"
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: 999,
                          background: "#dc2626",
                          color: "white",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 13,
                          fontWeight: 950,
                          lineHeight: "20px",
                        }}
                      >
                        !
                      </span>
                    ) : null}

                    {/* ✅ prioritaire : badge */}
                    {d?.prioritaire ? (
                      <span
                        className="pill"
                        style={{
                          background: "#fef2f2",
                          border: "1px solid #fecaca",
                          color: "#991b1b",
                          fontWeight: 950,
                        }}
                      >
                        PRIORITAIRE
                      </span>
                    ) : null}

                    <span className={pillClassFromStatut(statut)}>
                      {statut ? statut : "Nouveau"}
                    </span>
                  </div>
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                  <span className="pill pill--muted">
                    Transmission SIAO : {d.date_premiere_transmission || "-"}
                  </span>

                  {item === "flechage" && norm(name) === norm("ELISSA") && d.operateur ? (
                    <span
                      className="pill pill--muted"
                      style={{
                        color: operateurColor(d.operateur) || undefined,
                        fontWeight: 950,
                      }}
                    >
                      {String(d.operateur).trim()}
                    </span>
                  ) : null}

                  {d.numero_demande ? (
                    <span className="pill pill--muted">#{d.numero_demande}</span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
